import { createFileRoute } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/shingle-roofing";
const NAME = "Shingle Roofing";
const TITLE = "Shingle Roof Installation & Repair in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Asphalt shingle roof installation, repair, and replacement in Phoenix. Family-owned roofer since 1989 — class-rated shingles, real warranties, honest scope.";

const faqs = [
  {
    q: "How long do asphalt shingles last in Phoenix?",
    a: "Standard 3-tab shingles are typically 15–18 years in the Valley; architectural (dimensional) shingles 20–25 years; premium impact-rated shingles can reach 25–30. Phoenix sun is harder on shingles than almost any other US climate.",
  },
  {
    q: "Should I pick architectural or 3-tab shingles?",
    a: "Architectural in almost every case. The price difference is modest, the wind rating is significantly better, and the lifespan is meaningfully longer. We rarely recommend 3-tab on new installs in the Valley.",
  },
  {
    q: "Will impact-rated shingles lower my insurance?",
    a: "Many carriers offer a discount for Class 4 impact-rated shingles, especially in hail-prone areas. We can specify Class 4 product if you want to ask your insurer.",
  },
  {
    q: "How do I know if my shingles need repair vs. replacement?",
    a: "Localized damage (a few missing or cracked shingles, a damaged pipe boot) is repairable. Widespread granule loss, curling, or multiple wind events worth of damage usually means it's time to plan the replacement.",
  },
  {
    q: "Can you match existing shingles for repairs?",
    a: "Usually yes — we'll match by manufacturer, line, and color. Older or discontinued lines may require pulling from less-visible areas or a close color match.",
  },
];

export const Route = createFileRoute("/shingle-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="shingle-roofing"
      eyebrow="Shingle Roofing"
      h1="Shingle Roof Installation & Repair"
      subhead="Architectural and impact-rated asphalt shingle systems installed to manufacturer spec — by a Phoenix roofer that's been in the Valley since 1989."
      heroBullets={[
        "Architectural and Class 4 impact-rated",
        "Manufacturer-certified installations",
        "Full re-roofs and section repairs",
        "Written, line-item estimates",
      ]}
      whatIs={{
        heading: "Asphalt shingle roofing in Phoenix",
        body: (
          <>
            <p>
              Asphalt shingles are the most common residential roof in America — they're affordable,
              fast to install, and well-understood. In Phoenix they're a perfectly valid choice on
              steeper-pitched roofs, but the Valley sun is unforgiving and the product you pick
              matters more here than almost anywhere else in the country.
            </p>
            <p>
              We default to architectural (dimensional) shingles for almost every Phoenix
              installation. They carry better wind ratings, hold up significantly better to UV,
              and last meaningfully longer than legacy 3-tab products. For monsoon and hail
              exposure, Class 4 impact-rated shingles are worth a serious look — they can extend
              roof life and may qualify for an insurance discount.
            </p>
            <p>
              Just as important as the shingle itself: the underlayment, the ice-and-water shield
              at valleys and penetrations, the drip edge, and the flashings. A premium shingle on
              a sloppy install will fail before a basic shingle on a tight one.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Why Phoenix is hard on shingles",
        body: (
          <>
            <p>
              Asphalt shingles age primarily from UV and thermal cycling. Phoenix delivers more
              of both than almost any climate in the country.
            </p>
            <ul>
              <li>
                <strong>UV exposure:</strong> Six months a year at UV index 11+ accelerates the
                breakdown of the asphalt binder that holds granules in place. Granule loss is what
                you're seeing when an old roof's surface looks "rough" or "patchy."
              </li>
              <li>
                <strong>Thermal cycling:</strong> 60°F+ daily swings flex shingles, loosen
                fasteners, and break the sealant bond between shingle courses.
              </li>
              <li>
                <strong>Monsoon wind:</strong> Microbursts and downdrafts lift shingles that have
                lost their sealant bond. Wind-rated shingles and quality nail placement matter.
              </li>
              <li>
                <strong>Hail:</strong> Less common in Phoenix proper but real in Mesa, Sun City,
                and East Valley areas. Impact-rated shingles dramatically reduce hail damage.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our shingle roofing process",
        steps: [
          {
            title: "Inspection and product specification",
            body: "We walk the roof, document the deck and existing system, and recommend a specific shingle and underlayment combination for your home and budget.",
          },
          {
            title: "Tear-off and deck repair",
            body: "Old roof comes off; we evaluate and repair any rotted decking or damaged framing before the new system goes down.",
          },
          {
            title: "Underlayment and flashings",
            body: "Synthetic underlayment, ice-and-water shield at valleys and penetrations, new drip edge, fresh pipe boots, and proper flashings.",
          },
          {
            title: "Shingle install and cleanup",
            body: "Shingles installed to manufacturer pattern with the correct nail count and placement. Property is fully cleaned including magnet sweep for nails.",
          },
        ],
      }}
      faqs={faqs}
      ctaTitle="Time for a new shingle roof?"
      ctaBody="Schedule a free inspection and we'll quote your re-roof with line-item pricing — including product, warranty, and timeline."
    />
  );
}
